import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api/client';
import AppLayout from '../components/AppLayout';
import EquityChart from '../components/EquityChart';
import MonthlyHeatmap from '../components/MonthlyHeatmap';
import DrawdownChart from '../components/DrawdownChart';
import RollingSharpChart from '../components/RollingSharpChart';
import VixChart from '../components/VixChart';
import AnalyticsTab from '../components/AnalyticsTab';
import BenchmarkPanel from '../components/BenchmarkPanel';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../components/ui/dropdown-menu';
import { ArrowUp, ChevronRight, Download } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import { buildMarkdownReport, buildHtmlReport, downloadTextFile } from '../utils/wfReport';
import { wfHeadline } from '../lib/analytics';
import MetricInfo from '../components/MetricInfo';

// A run sits in 'pending' from the moment the row is created until the worker
// subprocess boots and flips it to 'running'. Poll through both, or a page
// opened inside that window renders empty and never refreshes itself.
const IN_PROGRESS = new Set(['pending', 'running']);

function fmtDate(s) { return s ? s.slice(0, 10) : '—'; }
function fmtPct(v)  { return v != null ? `${v > 0 ? '+' : ''}${v.toFixed(2)}%` : '—'; }
function fmtNum(v)  { return v != null ? v.toFixed(2) : '—'; }
function fmtMoney(v){ return v != null ? `₹${Math.round(v).toLocaleString()}` : '—'; }

function MetricsRow({ metrics }) {
  if (!metrics) return null;
  return (
    <div className="flex gap-4 flex-wrap text-xs mb-3 p-3 bg-card shadow-lg border border-border rounded-lg">
      <span className="flex flex-col"><em className="text-[10px] uppercase font-semibold text-muted-foreground not-italic mb-0.5">Return</em><strong className={`text-sm ${metrics.total_return_pct >= 0 ? 'text-win' : 'text-loss'}`}>{fmtPct(metrics.total_return_pct)}</strong></span>
      <span className="flex flex-col"><em className="text-[10px] uppercase font-semibold text-muted-foreground not-italic mb-0.5 flex items-center gap-1">Sharpe<MetricInfo metric="sharpe_ratio" /></em><strong className="text-sm text-foreground">{fmtNum(metrics.sharpe_ratio)}</strong></span>
      <span className="flex flex-col"><em className="text-[10px] uppercase font-semibold text-muted-foreground not-italic mb-0.5 flex items-center gap-1">Sortino<MetricInfo metric="sortino_ratio" /></em><strong className="text-sm text-foreground">{fmtNum(metrics.sortino_ratio)}</strong></span>
      <span className="flex flex-col"><em className="text-[10px] uppercase font-semibold text-muted-foreground not-italic mb-0.5 flex items-center gap-1">Exposure<MetricInfo metric="time_in_market_pct" /></em><strong className="text-sm text-foreground">{metrics.time_in_market_pct != null ? `${metrics.time_in_market_pct.toFixed(0)}%` : '—'}</strong></span>
      <span className="flex flex-col"><em className="text-[10px] uppercase font-semibold text-muted-foreground not-italic mb-0.5">Max DD</em><strong className="text-sm text-loss">-{fmtNum(metrics.max_drawdown_pct)}%</strong></span>
      <span className="flex flex-col"><em className="text-[10px] uppercase font-semibold text-muted-foreground not-italic mb-0.5">Win Rate</em><strong className="text-sm text-foreground">{metrics.win_rate_pct?.toFixed(1)}%</strong></span>
      <span className="flex flex-col"><em className="text-[10px] uppercase font-semibold text-muted-foreground not-italic mb-0.5">Trades</em><strong className="text-sm text-foreground">{metrics.total_trades}</strong></span>
    </div>
  );
}

function AggMetrics({ windows, wfRun }) {
  if (!windows.length) return null;
  const h = wfHeadline(wfRun, windows);
  if (!h) return null;

  const allTrades = windows.flatMap(w => w.live_trades || []);
  const wins      = allTrades.filter(t => t.pnl > 0).length;
  const winRate   = allTrades.length ? (wins / allTrades.length) * 100 : 0;
  const switches  = windows.filter(w => w.pair_changed).length;

  // Sharpe leads the headline because Sharpe is what the ranker optimises on.
  // Exposure sits beside it: the curve has a point per trading day whether or
  // not capital was deployed, so idle days drag the ratio toward a measure of
  // time in the market rather than of the trades themselves.
  const idle = h.time_in_market_pct != null && h.time_in_market_pct < 50;

  const bs = h.bootstrap;
  const ciLabel = bs?.sharpe_p2_5 != null
    ? `95% CI ${fmtNum(bs.sharpe_p2_5)} – ${fmtNum(bs.sharpe_p97_5)}`
    : null;

  const tiles = [
    { key: 'Portfolio Value', val: fmtMoney(h.final_value), sub: `from ${fmtMoney(h.initial_funds)}` },
    {
      key: 'Total Return',
      info: 'total_return_pct',
      val: fmtPct(h.total_return_pct),
      color: h.total_return_pct >= 0 ? 'text-win' : 'text-loss',
    },
    {
      key: h.cagr_is_annualized ? 'CAGR' : 'Period Return',
      info: 'cagr_pct',
      val: fmtPct(h.cagr_pct),
      color: h.cagr_pct >= 0 ? 'text-win' : 'text-loss',
      // Under a year there is nothing to annualize honestly, so the figure is
      // the period return and is labelled as such.
      sub: h.cagr_is_annualized ? 'annualised' : `${h.period_years?.toFixed(2)}y — not annualised`,
    },
    {
      key: 'Sharpe',
      info: 'sharpe_ratio',
      val: fmtNum(h.sharpe_ratio),
      sub: ciLabel,
      // The confidence interval needs its own explainer — it sits in the
      // sub-line and is the harder of the two numbers to read.
      subInfo: ciLabel ? 'sharpe_ci' : null,
    },
    { key: 'Sortino', info: 'sortino_ratio', val: fmtNum(h.sortino_ratio) },
    {
      key: 'Max Drawdown',
      info: 'max_drawdown_pct',
      val: h.max_drawdown_pct != null ? `-${h.max_drawdown_pct.toFixed(2)}%` : '—',
      color: 'text-loss',
    },
    {
      key: 'Exposure',
      info: 'time_in_market_pct',
      val: h.time_in_market_pct != null ? `${h.time_in_market_pct.toFixed(1)}%` : '—',
      sub: h.avg_exposure_pct != null ? `${h.avg_exposure_pct.toFixed(1)}% avg deployed` : 'not recorded',
      subInfo: h.avg_exposure_pct != null ? 'avg_exposure_pct' : null,
      color: idle ? 'text-loss' : undefined,
    },
    {
      key: 'Expectancy',
      info: 'expectancy_r',
      val: h.expectancy_r != null ? `${h.expectancy_r.toFixed(2)}R` : '—',
      sub: h.expectancy_pct != null ? `${fmtPct(h.expectancy_pct)} / trade` : null,
    },
    { key: 'Win Rate', info: 'win_rate_pct', val: `${winRate.toFixed(1)}%` },
    { key: 'Total Trades', val: allTrades.length },
    { key: 'Pair Switches', val: switches },
  ];

  // Segments where no candidate scored above zero. Previously these silently
  // traded whichever pair came first in DB order.
  const fellBack = windows.filter(w => w.fallback_pair_used);
  if (fellBack.length) {
    tiles.push({
      key: 'Fallback Segments',
      val: `${fellBack.length} / ${windows.length}`,
      color: 'text-amber-600',
      sub: 'ranking refused',
    });
  }

  const refused = windows.filter(w => w.screener_name == null);
  if (refused.length) {
    tiles.push({
      key: 'Refused Segments',
      val: `${refused.length} / ${windows.length}`,
      color: 'text-loss',
      sub: 'no credible candidate',
    });
  }

  return (
    <div className="flex flex-col gap-2 mb-6">
      <div className="grid grid-cols-2 md:flex md:flex-wrap gap-3">
        {tiles.map(({ key, val, sub, color, info, subInfo }) => (
          <div key={key} className="flex flex-col gap-0.5 p-3 bg-card shadow-lg rounded-lg border border-border md:min-w-[140px] flex-1">
            <span className="text-[10px] uppercase font-semibold text-muted-foreground flex items-center gap-1">
              {key}{info && <MetricInfo metric={info} />}
            </span>
            <span className={`text-lg md:text-xl font-bold ${color || 'text-foreground'}`}>{val}</span>
            {sub && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                {sub}{subInfo && <MetricInfo metric={subInfo} />}
              </span>
            )}
          </div>
        ))}
      </div>

      {idle && (
        <p className="text-[11px] text-muted-foreground px-1">
          Capital was deployed on {h.time_in_market_pct.toFixed(0)}% of trading days. Idle days
          enter the return series as zeros, which drags Sharpe and Sortino above toward a measure
          of time in the market rather than of the trades
          {h.active_sharpe_ratio != null && ` — over deployed days only, Sharpe is ${fmtNum(h.active_sharpe_ratio)}`}.
        </p>
      )}
      {fellBack.length > 0 && (
        <p className="text-[11px] text-muted-foreground px-1">
          {fellBack.length} of {windows.length} segments had no combination scoring above zero and traded the
          nominated fallback pair instead of sitting out. Those segments were not selected on merit — judge the
          run with and without them before trusting the headline.
        </p>
      )}
      {refused.length > 0 && (
        <p className="text-[11px] text-muted-foreground px-1">
          {refused.length} of {windows.length} segments had no candidate scoring above zero and took
          no new entries{refused[0].skipped_reason ? ` (${refused[0].skipped_reason})` : ''}. Capital
          sat in cash for those periods, which the exposure figure above reflects.
        </p>
      )}
      {bs?.sharpe_beats_zero === false && (
        <p className="text-[11px] text-muted-foreground px-1">
          The 95% bootstrap interval for Sharpe includes zero, so this run has not been shown to
          beat a flat return on this sample.
        </p>
      )}
    </div>
  );
}

function RunProgress({ wfRun }) {
  const total = wfRun.total_windows || 0;
  const current = wfRun.current_window || 0;
  const pct = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0;
  const phaseLabel = wfRun.status === 'pending'
    ? 'Starting run'
    : wfRun.phase === 'ranking' ? 'Ranking windows'
    : wfRun.phase === 'simulating' ? 'Simulating live windows'
    : 'Preparing data';

  return (
    <div className="flex flex-col gap-2 p-4 bg-card shadow-lg border border-border rounded-lg">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-foreground flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
          {phaseLabel}
        </span>
        <span className="text-muted-foreground font-mono">
          {total > 0 ? `${current} / ${total}` : 'queued…'}
        </span>
      </div>
      <div className="w-full h-1.5 bg-surface-hover rounded-full overflow-hidden">
        <div
          className="h-full bg-primary transition-all duration-500"
          style={{ width: `${total > 0 ? pct : 5}%` }}
        />
      </div>
    </div>
  );
}

function ScrollToTop() {
  const [isVisible, setIsVisible] = useState(false);

  // Check scroll position to show/hide the button
  useEffect(() => {
    const toggleVisibility = () => {
      // Show button if scrolled down more than 300px
      if (window.scrollY > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  // Smooth scroll back to the top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <button
      onClick={scrollToTop}
      className={`fixed bottom-6 right-6 z-50 p-3 bg-primary text-primary-foreground rounded-full shadow-lg hover:bg-primary/90 transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
      }`}
      aria-label="Scroll to top"
    >
      <ArrowUp className="w-5 h-5" />
    </button>
  );
}

export default function HelmWFResult() {
  const { id, wfRunId } = useParams();

  const [wfRun,   setWfRun]   = useState(null);
  const [windows, setWindows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [selWin,  setSelWin]  = useState(null);

  const hasSelectedRef = useRef(false);

  useEffect(() => {
    hasSelectedRef.current = false;
    let cancelled = false;
    let timer = null;

    const fetchOnce = () => api.get(`/helms/${id}/wf-runs/${wfRunId}`)
      .then(res => {
        if (cancelled) return;
        setWfRun(res.data.wf_run);
        setWindows(res.data.windows);
        if (res.data.windows.length && !hasSelectedRef.current) {
          hasSelectedRef.current = true;
          setSelWin(0);
        }
        if (IN_PROGRESS.has(res.data.wf_run?.status)) {
          timer = setTimeout(fetchOnce, 3000);
        }
      })
      .catch(err => { if (!cancelled) setError(err.response?.data?.error || 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    fetchOnce();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [id, wfRunId]);

  const handleDownloadReport = (format) => {
    if (!wfRun) return;
    const base = `helm-${id}-wf-${wfRunId}`;
    if (format === 'md') {
      downloadTextFile(`${base}.md`, buildMarkdownReport(wfRun, windows), 'text/markdown');
    } else {
      downloadTextFile(`${base}.html`, buildHtmlReport(wfRun, windows), 'text/html');
    }
  };

  // Auto-scroll to trades on mobile when a window is selected
  const handleSelectWindow = (index) => {
    setSelWin(index);
    setTimeout(() => {
        document.getElementById('trades-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
  };

  const stitchedCurve = windows.flatMap(w => w.equity_curve || []);
  const selected = selWin != null ? windows[selWin] : null;

  if (loading) return (
    <AppLayout>
      <div className="p-6 max-w-5xl mx-auto flex flex-col gap-4">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-4 w-1/2" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-14 rounded" />)}
        </div>
        <Skeleton className="h-48 w-full rounded" />
        <div className="flex flex-col gap-2">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 w-full rounded" />)}
        </div>
      </div>
    </AppLayout>
  );
  if (error)   return <AppLayout><p className="p-8 text-sm text-destructive">{error}</p></AppLayout>;

  return (
    <AppLayout backTo={`/helms/${id}`} backLabel="← Back to Helm">
      <main className="p-2 md:p-6 flex flex-col gap-6 max-w-6xl mx-auto">

        {IN_PROGRESS.has(wfRun?.status) && <RunProgress wfRun={wfRun} />}
        {wfRun?.status === 'failed' && (
          <div className="p-3 rounded-lg border border-destructive/30 bg-destructive/5 text-sm text-destructive">
            Run failed{wfRun.error_msg ? `: ${wfRun.error_msg}` : '.'}
          </div>
        )}

        {/* Improved Metadata Header */}
        {wfRun && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="px-2.5 py-1 bg-card shadow-lg border border-border rounded-full font-medium text-foreground">
                {fmtDate(wfRun.wf_start)} → {fmtDate(wfRun.wf_end)}
              </span>
              <span className="px-2.5 py-1 bg-card shadow-lg border border-border rounded-full text-muted-foreground">
                {wfRun.training_days}d train / {wfRun.interval_days}d interval
              </span>
              <span className="px-2.5 py-1 bg-card shadow-lg border border-border rounded-full text-muted-foreground">
                Ranked by <strong className="text-foreground">{wfRun.rank_by}</strong>
              </span>
              <span className="px-2.5 py-1 bg-card shadow-lg border border-border rounded-full text-muted-foreground">
                {windows.length} Window{windows.length !== 1 ? 's' : ''}
              </span>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Download className="w-3.5 h-3.5" />
                  Download Report
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleDownloadReport('md')}>Markdown (.md)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDownloadReport('html')}>HTML (.html)</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        <Tabs defaultValue="overview" className="flex flex-col gap-6 ">
          <TabsList className="self-start flex-wrap shadow-lg">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="logs">Trades</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="flex flex-col gap-6">

        {wfRun && <AggMetrics windows={windows} wfRun={wfRun} />}

        {stitchedCurve.length > 0 && (
          <div className="flex flex-col gap-2 bg-card shadow-lg p-4 border border-border rounded-lg">
            <h3 className="text-sm font-semibold text-foreground">Portfolio Equity Curve</h3>
            <EquityChart
              data={stitchedCurve}
              initialFunds={wfRun?.initial_funds || 100000}
              benchmarkData={wfRun?.analytics?.benchmark_curve || []}
              height={260}
            />
          </div>
        )}

        {/* Tear-sheet analytics (populated after run completes) */}
        {wfRun?.analytics && (<>

          {wfRun.analytics.benchmark_metrics && (
            <BenchmarkPanel
              metrics={wfRun.analytics.benchmark_metrics}
              portfolioReturnPct={wfHeadline(wfRun, windows)?.total_return_pct}
            />
          )}
          
          {wfRun.analytics.vix_curve?.length > 0 && (
            <div className="flex flex-col gap-3 bg-card shadow-lg p-4 border border-border rounded-lg shadow-lg">
              <div className="flex items-baseline justify-between">
                <h3 className="text-sm font-semibold text-foreground">India VIX</h3>
                <span className="text-xs text-muted-foreground">amber &gt; 20 · red &gt; 25</span>
              </div>
              <VixChart data={wfRun.analytics.vix_curve} height={160} />
            </div>
          )}
          
          {wfRun.analytics.monthly_returns?.length > 0 && (
            <div className="flex flex-col gap-3 bg-card shadow-lg p-4 border border-border rounded-lg shadow-lg">
              <h3 className="text-sm font-semibold text-foreground">Monthly Returns</h3>
              <MonthlyHeatmap data={wfRun.analytics.monthly_returns} />
            </div>
          )}

          {wfRun.analytics.drawdown_curve?.length > 0 && (
            <div className="flex flex-col gap-3 bg-card shadow-lg p-4 border border-border rounded-lg shadow-lg">
              <h3 className="text-sm font-semibold text-foreground">Drawdown</h3>
              <DrawdownChart
                data={wfRun.analytics.drawdown_curve}
                stats={wfRun.analytics.drawdown_stats || {}}
                height={160}
              />
            </div>
          )}

          {wfRun.analytics.rolling_sharpe?.some(pt => pt.sharpe_3m != null) && (
            <div className="flex flex-col gap-3 bg-card shadow-lg p-4 border border-border rounded-lg shadow-lg">
              <h3 className="text-sm font-semibold text-foreground">Rolling Sharpe</h3>
              <RollingSharpChart data={wfRun.analytics.rolling_sharpe} height={200} />
            </div>
          )}
        </>)}

          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsTab windows={windows} wfRun={wfRun} />
          </TabsContent>

          <TabsContent value="logs" className="flex flex-col gap-6">

        {/* Windows Section */}
        <div className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold text-foreground">Walk-Forward Windows</h3>
          
          {/* DESKTOP WINDOWS TABLE */}
          <div className="hidden md:block overflow-hidden rounded-lg border border-border bg-card shadow-lg">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-border bg-surface-hover/50">
                  {[
                    { h: '#' }, { h: 'Live Period' }, { h: 'Selected Pair' }, { h: 'Switch' },
                    { h: 'Return', info: 'total_return_pct' },
                    { h: 'Sharpe', info: 'sharpe_ratio' },
                    { h: 'Sortino', info: 'sortino_ratio' },
                    { h: 'Exposure', info: 'time_in_market_pct' },
                    { h: 'Trades' }, { h: 'Funds' }, { h: '' },
                  ].map(({ h, info }) => (
                    <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">
                      <span className="flex items-center gap-1">{h}{info && <MetricInfo metric={info} />}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {windows.map((w, i) => {
                  const wm = w.live_metrics || {};
                  const isSelected = i === selWin;
                  return (
                    <tr key={w.id}
                      onClick={() => handleSelectWindow(i)}
                      className={`border-b border-border/50 cursor-pointer transition-colors ${
                        isSelected ? 'bg-primary/5 border-l-2 border-l-primary' : 'hover:bg-surface-hover border-l-2 border-l-transparent'
                      }`}>
                      <td className="px-4 py-3 text-muted-foreground">{w.window_num}</td>
                      <td className="px-4 py-3 font-mono text-xs text-foreground">{fmtDate(w.live_start)} → {fmtDate(w.live_end)}</td>
                      <td className="px-4 py-3 text-xs font-medium">
                        {w.screener_name ? (
                          <span className="flex items-center gap-1.5">
                            <span className="text-foreground">{w.screener_name} <span className="text-muted-foreground px-1">×</span> {w.trade_config_name}</span>
                            {w.fallback_pair_used && (
                              <span
                                title={w.skipped_reason || 'Ranking refused; nominated fallback pair used'}
                                className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-600 shrink-0"
                              >Fallback</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-muted-foreground italic" title={w.skipped_reason || ''}>No candidate — no new entries</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {w.pair_changed === true  && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-600">Changed</span>}
                        {w.pair_changed === false && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-win/15 text-win">Same</span>}
                        {w.pair_changed === null  && <span className="text-xs text-muted-foreground">—</span>}
                      </td>
                      <td className={`px-4 py-3 text-xs font-medium ${wm.total_return_pct >= 0 ? 'text-win' : 'text-loss'}`}>{fmtPct(wm.total_return_pct)}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{fmtNum(wm.sharpe_ratio)}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{fmtNum(wm.sortino_ratio)}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{wm.time_in_market_pct != null ? `${wm.time_in_market_pct.toFixed(0)}%` : '—'}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{wm.total_trades ?? '—'}</td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{fmtMoney(w.final_funds)}</td>
                      <td className="px-4 py-3 text-right">
                         <ChevronRight className={`w-4 h-4 transition-transform ${isSelected ? 'text-primary translate-x-1' : 'text-muted-foreground/30'}`} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* MOBILE WINDOWS CARDS */}
          <div className="flex flex-col gap-3 md:hidden">
            {windows.map((w, i) => {
              const wm = w.live_metrics || {};
              const isSelected = i === selWin;
              return (
                <div 
                  key={w.id} 
                  onClick={() => handleSelectWindow(i)}
                  className={`flex flex-col gap-2 p-3 rounded-lg border transition-all cursor-pointer ${
                    isSelected ? 'bg-primary/5 border-primary ring-1 ring-primary' : 'bg-surface-base border-border'
                  }`}
                >
                  <div className="flex justify-between items-center border-b border-border/50 pb-2">
                     <span className="text-xs font-medium text-foreground">Window {w.window_num}</span>
                     <span className="font-mono text-xs text-muted-foreground">{fmtDate(w.live_start)} → {fmtDate(w.live_end)}</span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                     <span className="text-sm font-semibold truncate">
                       {w.screener_name
                         ? <span className="text-foreground">{w.screener_name} <span className="text-muted-foreground text-xs font-normal">× {w.trade_config_name}</span>{w.fallback_pair_used && <span className="ml-1.5 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-600">Fallback</span>}</span>
                         : <span className="text-muted-foreground italic text-xs">No candidate — no new entries</span>}
                     </span>
                     {w.pair_changed === true  && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-600 shrink-0 ml-2">Changed</span>}
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-2 pt-2 border-t border-border/50">
                     <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Return</span><span className={`text-xs font-medium ${wm.total_return_pct >= 0 ? 'text-win' : 'text-loss'}`}>{fmtPct(wm.total_return_pct)}</span></div>
                     <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase flex items-center gap-1">Sharpe<MetricInfo metric="sharpe_ratio" /></span><span className="text-xs text-foreground">{fmtNum(wm.sharpe_ratio)}</span></div>
                     <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase flex items-center gap-1">Sortino<MetricInfo metric="sortino_ratio" /></span><span className="text-xs text-foreground">{fmtNum(wm.sortino_ratio)}</span></div>
                     <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase flex items-center gap-1">Exposure<MetricInfo metric="time_in_market_pct" /></span><span className="text-xs text-foreground">{wm.time_in_market_pct != null ? `${wm.time_in_market_pct.toFixed(0)}%` : '—'}</span></div>
                     <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Trades</span><span className="text-xs text-foreground">{wm.total_trades ?? '—'}</span></div>
                     <div className="flex flex-col"><span className="text-[10px] text-muted-foreground uppercase">Funds</span><span className="text-xs text-foreground font-mono">{fmtMoney(w.final_funds)}</span></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Selected Window Trades Section */}
        {selected && (
          <div id="trades-section" className="flex flex-col gap-3 mt-4 p-4 border border-border bg-surface-card rounded-lg scroll-mt-20">
            <div className="flex flex-col gap-1 mb-2">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                Window {selected.window_num} Trades
              </h3>
              <p className="text-sm text-muted-foreground">
                Strategy: <strong className="text-foreground">{selected.screener_name}</strong> using <strong className="text-foreground">{selected.trade_config_name}</strong>
              </p>
            </div>
            
            <MetricsRow metrics={selected.live_metrics} />
            
            {(selected.live_trades || []).length === 0 ? (
               <p className="text-sm text-muted-foreground py-4 text-center border border-dashed border-border rounded-lg">No trades executed in this window.</p>
            ) : (
              <>
                {/* DESKTOP TRADES TABLE */}
                <div className="hidden md:block overflow-x-auto rounded-sm border border-border bg-card shadow-lg">
                  <table className="w-full text-sm border-collapse bg-surface-base">
                    <thead>
                      <tr className="border-b border-border bg-surface-hover/50">
                        {['Symbol', 'Entry', 'Exit', 'Shares', 'Entry ₹', 'Exit ₹', 'P&L', 'P&L %', 'Hold', 'Reason'].map(h => (
                          <th key={h} className="text-left px-3 py-2.5 text-[11px] uppercase tracking-wider font-semibold text-muted-foreground whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {selected.live_trades.map((t, i) => (
                        <tr key={i} className="border-b border-border/50 hover:bg-surface-hover transition-colors">
                          <td className="px-3 py-2 text-sm font-semibold text-foreground">{t.symbol.replace('.NS', '')}</td>
                          <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{fmtDate(t.entry_date)}</td>
                          <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{fmtDate(t.exit_date)}</td>
                          <td className="px-3 py-2 text-xs text-foreground">{t.shares}</td>
                          <td className="px-3 py-2 font-mono text-xs text-muted-foreground">₹{t.entry_price?.toFixed(2)}</td>
                          <td className="px-3 py-2 font-mono text-xs text-muted-foreground">₹{t.exit_price?.toFixed(2)}</td>
                          <td className={`px-3 py-2 text-xs font-medium ${t.pnl > 0 ? 'text-win' : 'text-loss'}`}>
                            {t.pnl > 0 ? '+' : ''}₹{t.pnl?.toFixed(0)}
                          </td>
                          <td className={`px-3 py-2 text-xs font-medium ${t.pnl_pct > 0 ? 'text-win' : 'text-loss'}`}>{fmtPct(t.pnl_pct)}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">{t.holding_days}d</td>
                          <td className="px-3 py-2 text-[10px] text-muted-foreground uppercase">{t.exit_reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* MOBILE TRADES CARDS */}
                <div className="flex flex-col gap-2 md:hidden">
                  {selected.live_trades.map((t, i) => (
                    <div key={i} className="flex flex-col gap-2 p-3 bg-surface-base border border-border rounded-lg">
                       <div className="flex justify-between items-center border-b border-border/50 pb-2">
                          <span className="font-bold text-foreground">{t.symbol.replace('.NS', '')}</span>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded ${t.pnl > 0 ? 'bg-win/10 text-win' : 'bg-loss/10 text-loss'}`}>
                             {t.pnl > 0 ? '+' : ''}₹{t.pnl?.toFixed(0)} ({fmtPct(t.pnl_pct)})
                          </span>
                       </div>
                       <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-xs mt-1">
                          <div className="flex justify-between"><span className="text-muted-foreground">Entry</span><span className="font-mono">{fmtDate(t.entry_date)}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Exit</span><span className="font-mono">{fmtDate(t.exit_date)}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Entry ₹</span><span className="font-mono">{t.entry_price?.toFixed(2)}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Exit ₹</span><span className="font-mono">{t.exit_price?.toFixed(2)}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Shares</span><span className="font-mono">{t.shares}</span></div>
                          <div className="flex justify-between"><span className="text-muted-foreground">Reason</span><span className="uppercase text-[10px]">{t.exit_reason}</span></div>
                       </div>
                    </div>
                  ))}
                </div>
                <ScrollToTop />
              </>
            )}
          </div>
        )}

          </TabsContent>
        </Tabs>
      </main>
    </AppLayout>
  );
}